import React, { useEffect, useRef, useState } from "react";
import config from "../config";

/**
 * MCP server logo for the Admin table.
 *
 * A server's icon is resolved (domain favicon / MCP-advertised) in a background
 * thread when it's added, so the first request usually 404s before resolution
 * finishes. We auto-retry a few times so the logo appears on its own, without a
 * manual page refresh. Each attempt changes the `&v=` param so the request
 * isn't served from a stale browser cache.
 *
 * `version` is bumped by the parent on a manual upload/remove to force an
 * immediate refresh (and to reset the retry cycle).
 */
const MAX_RETRIES = 8;   // ~20s of polling at the interval below
const RETRY_MS = 2500;

export default function AdminMcpIcon({ url, version = 0, size = 24 }) {
  const [attempt, setAttempt] = useState(0);
  const [hidden, setHidden] = useState(false);
  const timerRef = useRef(null);

  // A different server, or a manual upload/remove (version bump), restarts the
  // retry cycle and reveals the image again.
  useEffect(() => {
    setAttempt(0);
    setHidden(false);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [url, version]);

  const src =
    `${config.api.baseUrl}/claude-agent/mcp/servers/icon` +
    `?url=${encodeURIComponent(url)}&v=${version}_${attempt}`;

  return (
    <img
      key={`${version}_${attempt}`}
      className="mcp-logo"
      src={src}
      alt=""
      width={size}
      height={size}
      style={{
        borderRadius: 5,
        objectFit: "contain",
        display: "block",
        visibility: hidden ? "hidden" : "visible",
      }}
      onLoad={() => setHidden(false)}
      onError={() => {
        // Not resolved yet (404) — hide and retry until the background
        // resolution lands or we hit the cap.
        setHidden(true);
        if (attempt < MAX_RETRIES) {
          timerRef.current = setTimeout(() => setAttempt((a) => a + 1), RETRY_MS);
        }
      }}
    />
  );
}
